import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { quantPaperPositions, quantPaperClosePosition, getErrorMessage } from '../../lib/api';

/**
 * Tabella posizioni paper Alpaca aperte.
 * Polling automatico ogni 30s. Forza refetch quando `refreshSignal` cambia.
 *
 * Props:
 *  - refreshSignal: number|timestamp — se cambia, forza un refetch immediato
 */
const fmtPrice = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
const fmtPct = (v) => {
  if (v == null) return '—';
  const n = Number(v) * 100;
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
};
const fmtPnl = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  return `${n >= 0 ? '+' : ''}$${Math.abs(n).toFixed(2)}`;
};

const PositionsTable = ({ refreshSignal }) => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [providerMissing, setProviderMissing] = useState(false);
  const [closingSymbol, setClosingSymbol] = useState(null);
  const [confirmClose, setConfirmClose] = useState(null);
  const timerRef = useRef(null);

  const fetchPositions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await quantPaperPositions();
      setPositions(r.data?.items || []);
      setProviderMissing(false);
    } catch (e) {
      if (e.response?.status === 503) {
        setProviderMissing(true);
        setPositions([]);
      } else {
        setError(getErrorMessage(e, 'Errore posizioni'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh 30s
  useEffect(() => {
    fetchPositions();
    timerRef.current = setInterval(fetchPositions, 30000);
    return () => clearInterval(timerRef.current);
  }, [fetchPositions]);

  // Refetch quando il segnale esterno cambia (es. ordine appena piazzato)
  useEffect(() => {
    if (refreshSignal == null) return;
    fetchPositions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const handleClose = async (symbol) => {
    setClosingSymbol(symbol);
    try {
      await quantPaperClosePosition(symbol);
      toast.success(`Posizione ${symbol} chiusa`);
      await fetchPositions();
    } catch (e) {
      toast.error(getErrorMessage(e, `Errore chiusura ${symbol}`));
    } finally {
      setClosingSymbol(null);
    }
  };

  if (providerMissing) {
    return (
      <Card className="border-amber-500/30 bg-amber-50/40 dark:bg-amber-950/20">
        <CardContent className="py-4 flex items-start gap-2 text-sm">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Alpaca non configurato</p>
            <p className="text-muted-foreground text-xs mt-0.5">
              Per visualizzare le posizioni paper configura il provider Alpaca.{' '}
              <a href="/admin/market-providers" className="text-primary hover:underline">
                Configura qui
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-500/30">
        <CardContent className="py-4 text-sm text-red-600">{error}</CardContent>
      </Card>
    );
  }

  if (!loading && positions.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          <p className="font-medium">Nessuna posizione aperta.</p>
          <p className="text-xs mt-1">
            Apri una posizione dal segnale Live nel chart.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      {/* Header con refresh manuale e stato */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {loading && !positions.length ? (
            <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Caricamento…</span>
          ) : (
            <span>{positions.length} posizion{positions.length !== 1 ? 'i' : 'e'} aperta{positions.length !== 1 ? '' : ''}</span>
          )}
        </span>
        <Button variant="ghost" size="sm" onClick={fetchPositions} disabled={loading} className="h-7 px-2" aria-label="Aggiorna posizioni">
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </div>

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-mono text-xs">Symbol</TableHead>
              <TableHead className="text-right text-xs">Qty</TableHead>
              <TableHead className="text-right text-xs">Avg Entry</TableHead>
              <TableHead className="text-right text-xs">Corrente</TableHead>
              <TableHead className="text-right text-xs">Mkt Value</TableHead>
              <TableHead className="text-right text-xs">P&amp;L</TableHead>
              <TableHead className="text-right text-xs">P&amp;L %</TableHead>
              <TableHead className="text-xs"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {positions.map((pos) => {
              const pnl = Number(pos.unrealized_pl ?? pos.unrealized_plpc == null ? null : null);
              const rawPnl = pos.unrealized_pl;
              const rawPnlPct = pos.unrealized_plpc;
              const pnlNum = rawPnl != null ? Number(rawPnl) : null;
              const pnlPctNum = rawPnlPct != null ? Number(rawPnlPct) : null;
              const isPositive = pnlNum != null && pnlNum >= 0;

              return (
                <TableRow key={pos.symbol}>
                  <TableCell>
                    <span className="font-mono font-semibold text-sm">{pos.symbol}</span>
                    {pos.strategy && (
                      <Badge variant="outline" className="ml-2 text-[9px]">{pos.strategy}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-mono text-sm">
                    {pos.qty}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-mono text-sm">
                    {fmtPrice(pos.avg_entry_price ?? pos.cost_basis)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-mono text-sm">
                    {fmtPrice(pos.current_price)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-mono text-sm">
                    {fmtPrice(pos.market_value)}
                  </TableCell>
                  <TableCell className={`text-right tabular-nums font-mono text-sm font-medium ${
                    pnlNum == null ? 'text-muted-foreground' : isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                  }`}>
                    {fmtPnl(rawPnl)}
                  </TableCell>
                  <TableCell className={`text-right tabular-nums font-mono text-sm font-medium ${
                    pnlPctNum == null ? 'text-muted-foreground' : pnlPctNum >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                  }`}>
                    {fmtPct(rawPnlPct)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      disabled={closingSymbol === pos.symbol}
                      onClick={() => setConfirmClose(pos.symbol)}
                    >
                      {closingSymbol === pos.symbol ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        'Chiudi'
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <p className="text-[10px] text-muted-foreground text-right">Auto-refresh 30s</p>

      {/* Dialog conferma chiusura posizione */}
      <AlertDialog open={!!confirmClose} onOpenChange={(open) => { if (!open) setConfirmClose(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Chiudere posizione su {confirmClose}?</AlertDialogTitle>
            <AlertDialogDescription>
              L'ordine market sarà inviato ad Alpaca paper. L'operazione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmClose(null)}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                const sym = confirmClose;
                setConfirmClose(null);
                handleClose(sym);
              }}
            >
              Chiudi posizione
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PositionsTable;
